import { useState, type ReactNode } from 'react';
import { useGT } from 'gt-react';
import { Label } from 'src/components/ui/label';
import { Button } from 'src/components/ui/button';
import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { replaceEligibleEntityId } from './identify-party/identifyPartyFormConfig';

/**
 * Allowlist editor for identify_customer / identify_supplier.
 *
 * Empty means "match against the full organization catalog". A non-empty
 * list is the rollout knob: only these entities are considered.
 */
export function EligibleEntityIdsEditor({
  ids,
  onChange,
  renderPicker,
  addLabel,
  emptyDescription,
  sectionLabel,
  sectionHelp,
}: {
  ids: string[];
  onChange: (ids: string[]) => void;
  renderPicker: (
    id: string | undefined,
    onSelect: (next: string | undefined) => void,
  ) => ReactNode;
  addLabel: string;
  emptyDescription: ReactNode;
  sectionLabel: ReactNode;
  sectionHelp: ReactNode;
}) {
  const gt = useGT();
  const [draftKeys, setDraftKeys] = useState<string[]>([]);

  function commitId(next: string) {
    if (ids.includes(next)) return;
    onChange([...ids, next]);
  }

  return (
    <section className="space-y-3">
      <div>
        <Label className="text-sm">{sectionLabel}</Label>
        <p className="text-[11px] text-muted-foreground mt-0.5">
          {sectionHelp}
        </p>
      </div>

      <div className="space-y-2">
        {ids.map((id) => (
          <div key={id} className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              {renderPicker(id, (next) => {
                if (!next) {
                  onChange(ids.filter((current) => current !== id));
                  return;
                }
                const replaced = replaceEligibleEntityId({
                  ids,
                  currentId: id,
                  nextId: next,
                });
                if (replaced) onChange(replaced);
              })}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-destructive hover:text-destructive shrink-0"
              onClick={() => onChange(ids.filter((current) => current !== id))}
              aria-label={gt('Remove from allowlist', {
                context: 'workflow identify-entity eligible list',
              })}
            >
              <TrashIcon className="w-3 h-3" />
            </Button>
          </div>
        ))}

        {draftKeys.map((draftKey) => (
          <div key={draftKey} className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              {renderPicker(undefined, (next) => {
                setDraftKeys((current) =>
                  current.filter((key) => key !== draftKey),
                );
                if (next) commitId(next);
              })}
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 text-destructive hover:text-destructive shrink-0"
              onClick={() =>
                setDraftKeys((current) =>
                  current.filter((key) => key !== draftKey),
                )
              }
              aria-label={gt('Remove from allowlist', {
                context: 'workflow identify-entity eligible list',
              })}
            >
              <TrashIcon className="w-3 h-3" />
            </Button>
          </div>
        ))}

        {ids.length === 0 && draftKeys.length === 0 && (
          <p className="text-xs text-muted-foreground italic px-1">
            {emptyDescription}
          </p>
        )}
      </div>

      <Button
        variant="outline"
        size="sm"
        className="w-full"
        onClick={() =>
          setDraftKeys((current) => [...current, crypto.randomUUID()])
        }
      >
        <PlusIcon className="w-3 h-3 mr-1.5" />
        {addLabel}
      </Button>
    </section>
  );
}
